import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button, Card } from "@gradebook/ui";

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Optional custom fallback. Receives the caught error and a reset callback. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Catches render-time errors anywhere in the child tree and shows a friendly
 * fallback instead of a blank white screen. Errors thrown in event handlers,
 * async code, or SSR are NOT caught by React error boundaries by design.
 */
export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Surface the failure for logging/observability. Kept as console.error so
    // it shows up in the browser console and any error-reporting hook.
    console.error("Uncaught error in React tree:", error, errorInfo);
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    const { error } = this.state;

    if (error) {
      if (this.props.fallback) {
        return this.props.fallback(error, this.reset);
      }

      return (
        <div
          role="alert"
          className="flex min-h-screen items-center justify-center p-4"
        >
          <Card className="w-full max-w-md text-center" padding="lg">
            <h1 className="mb-2 text-xl font-semibold text-foreground">
              Something went wrong
            </h1>
            <p className="mb-6 text-sm text-muted-foreground">
              An unexpected error occurred. Try reloading the page — if the
              problem persists, contact your administrator.
            </p>
            <div className="flex justify-center gap-3">
              <Button variant="outline" onClick={this.reset}>
                Try again
              </Button>
              <Button onClick={() => window.location.reload()}>
                Reload page
              </Button>
            </div>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
